import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { getRoom, joinRoom } from "@/lib/api";
import { getSession, setSession, getStoredName, setStoredName, removeVisited, clearSession } from "@/lib/storage";
import Lobby from "@/components/game/Lobby";
import Game from "@/components/game/Game";

export default function Room() {
  const { code } = useParams();
  const upperCode = (code || "").toUpperCase();
  const nav = useNavigate();
  const [room, setRoom] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nameInput, setNameInput] = useState(getStoredName());
  const [needsName, setNeedsName] = useState(false);
  const pollRef = useRef(null);

  const fetchRoom = useCallback(async (pid) => {
    try {
      const { room } = await getRoom(upperCode, pid);
      setRoom(room);
    } catch (err) {
      if (err?.response?.status === 404) {
        // La sala fue eliminada o no existe — limpiar y volver al lobby
        removeVisited(upperCode);
        clearSession(upperCode);
        toast.error("La sala ya no existe");
        nav("/");
      }
    }
  }, [upperCode, nav]);

  // Initial load / rejoin logic
  useEffect(() => {
    (async () => {
      const sess = getSession(upperCode);
      if (sess?.player_id) {
        setPlayerId(sess.player_id);
        await fetchRoom(sess.player_id);
        setLoading(false);
        return;
      }
      // No session, try join with stored name
      const stored = getStoredName();
      if (stored) {
        try {
          const { room, player_id } = await joinRoom(upperCode, { player_name: stored });
          setSession(upperCode, { player_id, name: stored });
          setPlayerId(player_id);
          setRoom(room);
        } catch (err) {
          if (err?.response?.status === 404) {
            toast.error("Sala no encontrada");
            nav("/");
            return;
          }
          setNeedsName(true);
        }
      } else {
        setNeedsName(true);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upperCode]);

  // Polling
  useEffect(() => {
    if (!playerId) return;
    pollRef.current = setInterval(() => { fetchRoom(playerId); }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [playerId, fetchRoom]);

  const submitName = async (e) => {
    e.preventDefault();
    if (!nameInput.trim()) return;
    setStoredName(nameInput.trim());
    try {
      const { room, player_id } = await joinRoom(upperCode, { player_name: nameInput.trim() });
      setSession(upperCode, { player_id, name: nameInput.trim() });
      setPlayerId(player_id);
      setRoom(room);
      setNeedsName(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo unir");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center z-[2] relative">
        <div className="label-classified">CARGANDO CANAL SEGURO…</div>
      </div>
    );
  }

  if (needsName) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 z-[2] relative">
        <form onSubmit={submitName} className="w-full max-w-md border border-[color:var(--border)] bg-[color:var(--surface)] p-6 flex flex-col gap-4">
          <div className="label-classified">Sala {upperCode}</div>
          <h2 className="font-heading text-3xl uppercase">Identifícate</h2>
          <input
            data-testid="input-name-room"
            className="input-classified"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder="TU NOMBRE"
            maxLength={20}
          />
          <button data-testid="btn-confirm-name" className="btn-primary" type="submit">Unirse</button>
        </form>
      </div>
    );
  }

  if (!room) return null;

  const self = room.players.find((p) => p.id === playerId);
  if (!self) {
    // we were removed; re-enter name
    return (
      <div className="min-h-screen flex items-center justify-center z-[2] relative">
        <div className="label-classified">SESIÓN CADUCADA…</div>
      </div>
    );
  }

  return (
    <div className="relative z-[2] min-h-screen">
      {room.state === "lobby" ? (
        <Lobby room={room} self={self} refresh={() => fetchRoom(playerId)} />
      ) : (
        <Game room={room} self={self} refresh={() => fetchRoom(playerId)} />
      )}
    </div>
  );
}
